import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Upload, X, Loader2, AlertCircle, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UploadTile = ({ label, file, onFile, onRemove, testIdPrefix }) => {
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!file) { setPreview(null); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1.5">{label}</label>
      {preview ? (
        <div className="relative">
          <img src={preview} alt={label} data-testid={`${testIdPrefix}-preview`} className="w-full h-28 object-cover rounded-lg border border-gray-200" />
          <button type="button" onClick={onRemove} data-testid={`${testIdPrefix}-remove-btn`} className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1">
            <X size={11} />
          </button>
        </div>
      ) : (
        <label className="flex flex-col items-center justify-center h-28 border-2 border-dashed border-gray-300 hover:border-[#37b34a] rounded-lg cursor-pointer text-gray-500 hover:text-[#37b34a] transition-colors">
          <Upload size={16} />
          <span className="text-xs mt-1.5">Upload</span>
          <input type="file" accept="image/*,application/pdf" className="hidden" data-testid={`${testIdPrefix}-file-input`} onChange={(e) => onFile(e.target.files?.[0] || null)} />
        </label>
      )}
    </div>
  );
};

/** Inline resubmission form shown on the professional dashboard when
 *  verification_status === 'rejected'. Lets them see why and re-upload. */
const VerificationResubmit = ({ pro, proId, onResubmitted }) => {
  const [expanded, setExpanded] = useState(false);
  const [idFile, setIdFile] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');

  const uploadDoc = async (file, docType) => {
    const fd = new FormData();
    fd.append('doc_type', docType);
    fd.append('file', file);
    const res = await axios.post(`${API}/professionals/upload-verification-doc`, fd, { withCredentials: true });
    return res.data.path;
  };

  const handleResubmit = async () => {
    if (!idFile || !selfieFile) {
      setError('Please upload both a new ID document and a new selfie.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      setProgress('Uploading ID document…');
      const idDocumentPath = await uploadDoc(idFile, 'id_document');
      setProgress('Uploading selfie…');
      const selfiePath = await uploadDoc(selfieFile, 'selfie');
      setProgress('Resubmitting…');
      await axios.put(
        `${API}/professionals/${proId}/verification-docs`,
        { id_document_path: idDocumentPath, selfie_path: selfiePath },
        { withCredentials: true }
      );
      setIdFile(null);
      setSelfieFile(null);
      setExpanded(false);
      onResubmitted?.();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not resubmit your documents. Please try again.');
    } finally {
      setSubmitting(false);
      setProgress('');
    }
  };

  return (
    <div data-testid="verification-rejected-banner" className="mb-6 bg-red-50 border border-red-200 text-red-800 text-sm px-4 py-3 rounded-xl">
      <p className="font-semibold">Identity verification rejected</p>
      <p className="text-red-700 mt-0.5">{pro.verification_rejection_reason || 'Please contact support for details.'}</p>

      {!expanded ? (
        <button
          type="button" onClick={() => setExpanded(true)} data-testid="open-resubmit-verification-btn"
          className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-red-700 hover:text-red-900 underline"
        >
          <RefreshCw size={12} /> Resubmit documents
        </button>
      ) : (
        <div className="mt-3 bg-white border border-red-100 rounded-lg p-4 space-y-3">
          {error && (
            <div data-testid="resubmit-error" className="bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded-lg flex items-start gap-2">
              <AlertCircle size={13} className="flex-shrink-0 mt-0.5" /> {error}
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            <UploadTile label="New ID Document" file={idFile} onFile={setIdFile} onRemove={() => setIdFile(null)} testIdPrefix="resubmit-id-document" />
            <UploadTile label="New Selfie" file={selfieFile} onFile={setSelfieFile} onRemove={() => setSelfieFile(null)} testIdPrefix="resubmit-selfie" />
          </div>
          <div className="flex gap-2">
            <button
              type="button" onClick={() => setExpanded(false)}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold px-4 py-2 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="button" onClick={handleResubmit} disabled={submitting}
              data-testid="submit-resubmit-btn"
              className="flex-1 flex items-center justify-center gap-2 bg-[#37b34a] hover:bg-[#2d9a3e] disabled:bg-gray-300 text-white text-sm font-semibold px-4 py-2 rounded-lg"
            >
              {submitting ? <><Loader2 size={14} className="animate-spin" /> {progress || 'Submitting…'}</> : 'Resubmit for Review'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerificationResubmit;
